import { create } from 'zustand';
import { api } from '@/shared/api/client';

// Active branch the user has selected from the topbar picker.
//
// The picker writes to `currentBranchId` (null = "All branches the user
// can see"), and the api client reads it on every request to send the
// X-Branch-Id header. Branches list is hydrated once at login.
//
// Persistence: `currentBranchId` lives in localStorage under
// `mrpcomp:current-branch:<userId>` so a refresh keeps the picker
// state. The user's `default_branch_id` (server-side) is the initial
// pick on first login of a new session.

const STORAGE_KEY = (userId: string) => `mrpcomp:current-branch:${userId}`;

export type Branch = {
  id: string; code: string; name: string;
  is_active: boolean; is_default: boolean;
};

type State = {
  branches: Branch[];
  currentBranchId: string | null;
  hydrated: boolean;
  hydrate: (userId?: string, defaultBranchId?: string | null) => Promise<void>;
  setCurrent: (id: string | null, userId?: string) => void;
  reset: () => void;
};

function loadInitial(userId?: string): string | null {
  if (!userId) return null;
  try { return localStorage.getItem(STORAGE_KEY(userId)) || null; } catch { return null; }
}

export const useBranchesStore = create<State>((set) => ({
  branches: [],
  currentBranchId: null,
  hydrated: false,
  hydrate: async (userId, defaultBranchId) => {
    try {
      const list = await api.get<Branch[]>('/branches');
      const active = list.filter((b) => b.is_active);
      const cached = loadInitial(userId);
      // First time on this device → pick: cached → user's default → null.
      let cur = cached;
      if (!cur) {
        if (defaultBranchId && active.some((b) => b.id === defaultBranchId)) {
          cur = defaultBranchId;
        } else {
          cur = null;
        }
      } else if (!active.some((b) => b.id === cur)) {
        // Cached branch no longer exists / inactive — fall back to default.
        cur = defaultBranchId && active.some((b) => b.id === defaultBranchId)
          ? defaultBranchId
          : null;
      }
      set({ branches: active, currentBranchId: cur, hydrated: true });
    } catch {
      // No branches endpoint reachable (e.g. 401). Stay empty; UI hides
      // the picker when there are < 2 branches anyway.
      set({ hydrated: true });
    }
  },
  setCurrent: (id, userId) => {
    set({ currentBranchId: id });
    if (userId) {
      try {
        if (id) localStorage.setItem(STORAGE_KEY(userId), id);
        else localStorage.removeItem(STORAGE_KEY(userId));
      } catch { /* quota — best effort */ }
    }
  },
  reset: () => set({ branches: [], currentBranchId: null, hydrated: false }),
}));

// Read-only accessor for non-React contexts (the api client).
export function getCurrentBranchId(): string | null {
  return useBranchesStore.getState().currentBranchId;
}
